import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { MoreVertical, Trash2 } from 'lucide-react';
import { typographyClasses } from '../../styles/typography';
import AutoResizeButton from '../ui/AutoResizeButton';
import { TalentPageCard, TalentPageCardContent } from '../ui/TalentPageCard';
import TiltedCard from '../ui/TiltedCard';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Button } from '../ui/Button';
import TalRevAPI from '../../services/api';

// Interface matching the API response structure
interface TalentCardData {
  id: string;
  status: string;
  progress: number;
  employee_name: string;
  current_stage: string;
  start_time: string;
  estimated_time_remaining: number;
  error: string | null;
  processing_duration: string | null;
  file_name: string;
  created_by: string;  // Owner email
  user_id: string;     // Owner user_id for filtering
  level: string;
  role: string;
  team: string;
  tenure: string;
  manager: string;
  tfcs: string | string[];
  average_score: number;
  total_dimensions: number;
  key_strengths: string[];
  development_areas: string[];
  confidence: number;
  overall_assessment: string;
  processed_date: string;
}

interface ApiBasedProfileCardProps {
  talentData: TalentCardData;
  className?: string;
  showRadarChart?: boolean;
  onDelete?: (id: string) => void;
  isAdmin?: boolean;
}

const ApiBasedProfileCard: React.FC<ApiBasedProfileCardProps> = ({
  talentData,
  className = '',
  showRadarChart = true,
  onDelete,
  isAdmin = false
}) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Parse TFCs from API response
  const parseTfcs = (tfcs: string | string[]) => {
    if (Array.isArray(tfcs)) {
      return tfcs.join(', ');
    } else if (typeof tfcs === 'string') {
      try {
        // Handle string representation of array like "['Cloud Operations']" or "['Networking TFC']"
        const cleanedString = tfcs.replace(/'/g, '"');
        const parsed = JSON.parse(cleanedString);
        return Array.isArray(parsed) ? parsed.join(', ') : tfcs;
      } catch {
        // If JSON parsing fails, try to extract content between brackets
        const match = tfcs.match(/\[(.*?)\]/);
        if (match) {
          return match[1].replace(/'/g, '').replace(/"/g, '');
        }
        return tfcs;
      }
    }
    return 'N/A';
  };

  const handleCardClick = () => {
    navigate(`/talents/${talentData.id}`);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      await TalRevAPI.deleteTalentCard(talentData.id);
      onDelete(talentData.id);
    } catch (error) {
      console.error('Failed to delete talent:', error);
      setDeleteError('Failed to delete talent');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`grid gap-4 sm:gap-6 ${
      showRadarChart ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'
    } ${className} h-full`}>
        {/* Profile Card */}
        <TalentPageCard 
          className="overflow-hidden h-full cursor-pointer hover:shadow-lg transition-shadow duration-200 flex flex-col"
          onClick={handleCardClick}
        >
          <div className="flex flex-col lg:flex-row min-h-full">
            {/* Top/Left Column - Image */}
            <div className="w-full lg:w-1/3 h-48 lg:h-auto lg:min-h-full bg-gray-100 flex-shrink-0">
              <TiltedCard
                imageSrc="/image.png"
                altText={`${talentData.employee_name} profile`}
                containerHeight="100%"
                containerWidth="100%"
                scaleOnHover={1.05}
                rotateAmplitude={8}
                className="rounded-t-xl lg:rounded-t-none lg:rounded-l-xl"
                objectFit="contain"
              />
            </div>
            
            {/* Bottom/Right Column - Content */}
            <TalentPageCardContent className="flex-1 pl-3 sm:pl-4 lg:pl-6 pr-2 sm:pr-3 lg:pr-4 py-3 sm:py-4 min-w-0 flex flex-col">
              {/* Header Section */}
              <div className="mb-6">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <h1 className={typographyClasses.heading.h1 + " mb-1"}>{talentData.employee_name}</h1>
                    <p className={typographyClasses.body.base + " text-gray-600 mb-1"}>{talentData.role}</p>
                    {isAdmin && talentData.created_by && (
                      <p className={typographyClasses.body.small + " text-gray-400"}>
                        Uploaded by {talentData.created_by.split('@')[0]}
                      </p>
                    )}
                  </div>
                  {onDelete && (
                    <div className="flex items-center space-x-2 ml-4">
                      {deleteError && (
                        <span className="text-red-500 text-[var(--font-size-sm)]">{deleteError}</span>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-gray-100"
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={handleDelete}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            disabled={isDeleting}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>

              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`${typographyClasses.label.uppercase} truncate`}>Level</p>
                    <p className={`${typographyClasses.label.primary} truncate`}>{talentData.level}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`${typographyClasses.label.uppercase} truncate`}>TFCs</p>
                    <p className={`${typographyClasses.label.primary} truncate`}>
                      {parseTfcs(talentData.tfcs)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`${typographyClasses.label.uppercase} truncate`}>Average Score</p>
                    <p className={`${typographyClasses.label.primary} truncate`}>{talentData.average_score}/5.0</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full mt-auto pt-4">
                <AutoResizeButton
                  variant="primary"
                  text="Talent card"
                  shortText="Card"
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2-2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  }
                  size="profile"
                  className="flex-1 sm:flex-1"
                  onClick={() => {
                    handleCardClick();
                  }}
                />
                
                <AutoResizeButton
                  variant="secondary"
                  text="Phone tool"
                  shortText="Phone"
                  icon={
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  }
                  size="profile"
                  className="flex-1 sm:flex-1"
                  onClick={() => {}}
                />
              </div>
            </TalentPageCardContent>
          </div>
        </TalentPageCard>
        
        {/* Note: Radar Chart removed as it requires dimension scores data structure not available in API */}
        {showRadarChart && (
          <TalentPageCard className="p-3 sm:p-4 lg:p-6 overflow-hidden h-full min-h-[300px] sm:min-h-[400px]">
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <p className={typographyClasses.body.large}>Radar Chart</p>
                <p className={typographyClasses.body.small}>Requires dimension scores data structure</p>
                <p className={typographyClasses.body.small}>Available: {talentData.total_dimensions} dimensions</p>
                <p className={typographyClasses.body.small}>Average Score: {talentData.average_score}/5.0</p>
              </div>
            </div>
          </TalentPageCard>
        )}
      </div>
  );
};

export default ApiBasedProfileCard;
export type { TalentCardData };